import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Power, Trash2 } from 'lucide-react'
import {
  ProjectDetailsCard,
  EditProjectDialog,
  DeleteProjectDialog,
  DecommissionProjectDialog,
  ProjectSectionHeader,
} from '@/components/projects'
import { Button } from '@/components/ui/button'
import { useAuth, useProject, useProjectTeamMembers } from '@/hooks'
import { Permissions } from '@/lib/permissions'

export function ProjectOverviewPage() {
  const { projectId } = useParams()
  const { data: project } = useProject(projectId)
  const { data: teamMembers = [] } = useProjectTeamMembers(projectId)
  const { hasPermission } = useAuth()

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [decommissionDialogOpen, setDecommissionDialogOpen] = useState(false)

  const canEdit = hasPermission(Permissions.Host.Project.Update)
  const canDelete = hasPermission(Permissions.Host.Project.Delete)
  const canDecommission = hasPermission(Permissions.Host.Project.Decommission)

  if (!project) {
    return (
      <div
        className="rounded-lg border border-dashed p-12 text-center text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        Loading project...
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ProjectSectionHeader sectionName="Overview">
        {canDelete && (
          <Button
            variant="ghost"
            onClick={() => setDeleteDialogOpen(true)}
            data-testid="delete-project-button"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        )}
        {canDecommission && (
          <Button
            variant="ghost"
            onClick={() => setDecommissionDialogOpen(true)}
            className="text-danger-foreground hover:bg-danger hover:text-danger-foreground"
            data-testid="decommission-project-button"
          >
            <Power className="h-4 w-4 mr-2" />
            Decommission
          </Button>
        )}
      </ProjectSectionHeader>

      <ProjectDetailsCard
        project={project}
        teamMembers={teamMembers}
        canEdit={canEdit}
        onEdit={() => setEditDialogOpen(true)}
      />

      <EditProjectDialog
        project={project}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />

      <DeleteProjectDialog
        project={project}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />

      <DecommissionProjectDialog
        project={project}
        open={decommissionDialogOpen}
        onOpenChange={setDecommissionDialogOpen}
      />
    </div>
  )
}
